import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Mail, 
  MailOpen,
  Search,
  ExternalLink,
  Clock,
  CheckCircle,
  Eye,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleInbox = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Password from environment variable
  const INBOX_PASSWORD = process.env.REACT_APP_INBOX_PASSWORD || 'inbox2024';

  useEffect(() => {
    // Check if already authenticated in session
    const authStatus = sessionStorage.getItem('inbox_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      loadInquiries();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      filterInquiries();
    }
  }, [inquiries, statusFilter, searchQuery, isAuthenticated]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === INBOX_PASSWORD) {
      sessionStorage.setItem('inbox_authenticated', 'true');
      setIsAuthenticated(true);
      loadInquiries();
    } else {
      toast.error('Incorrect password');
    }
  };

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/inquiries`);
      setInquiries(response.data.inquiries);
    } catch (error) {
      console.error('Failed to load inquiries:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let filtered = [...inquiries];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(inq => inq.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(inq => 
        inq.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredInquiries(filtered);
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${inquiryId}`, {
        status: newStatus
      });
      toast.success(`Marked as ${newStatus}`);
      loadInquiries();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const openEmailClient = (inquiry) => {
    const subject = encodeURIComponent(`Re: ${inquiry.subject}`);
    const body = encodeURIComponent(`Hi ${inquiry.name},\n\nThank you for contacting RouteFlow AI.\n\n`);
    window.location.href = `mailto:${inquiry.email}?subject=${subject}&body=${body}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4'>
        <Card className='w-full max-w-md border-0 shadow-xl'>
          <CardHeader className='text-center'>
            <div className='w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Mail className='w-8 h-8 text-white' />
            </div>
            <CardTitle className='text-2xl'>Contact Messages Inbox</CardTitle>
            <CardDescription>Enter password to view messages</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className='space-y-4'>
              <div>
                <Input
                  type='password'
                  placeholder='Enter inbox password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className='text-center text-lg'
                  autoFocus
                />
              </div>
              <Button 
                type='submit' 
                className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
              >
                Access Inbox
              </Button>
            </form>
            <p className='text-xs text-center text-gray-500 mt-4'>
              Contact administrator if you don't have the password
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Inbox Screen
  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Header */}
      <div className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4'>
          <div className='flex justify-between items-center'>
            <div className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center'>
                <Mail className='w-6 h-6 text-white' />
              </div>
              <div>
                <h1 className='text-2xl font-bold text-gray-900'>Contact Messages</h1>
                <p className='text-sm text-gray-600'>
                  {inquiries.filter(i => i.status === 'pending').length} pending messages
                </p>
              </div>
            </div>
            <Button 
              variant='outline' 
              onClick={() => {
                sessionStorage.removeItem('inbox_authenticated');
                setIsAuthenticated(false);
              }}
            >
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Search & Filter */}
        <div className='mb-6 space-y-4'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
            <Input
              type='text'
              placeholder='Search by name, email, or subject...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-10'
            />
          </div>
          <div className='flex space-x-2'>
            <Button 
              size='sm'
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('all')}
            >
              All ({inquiries.length})
            </Button>
            <Button 
              size='sm'
              variant={statusFilter === 'pending' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('pending')}
            >
              Pending ({inquiries.filter(i => i.status === 'pending').length})
            </Button>
            <Button 
              size='sm'
              variant={statusFilter === 'replied' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('replied')}
            >
              Replied ({inquiries.filter(i => i.status === 'replied').length})
            </Button>
          </div>
        </div>

        {loading ? (
          <div className='flex items-center justify-center py-12'>
            <Loader2 className='w-8 h-8 animate-spin text-emerald-600' />
          </div>
        ) : filteredInquiries.length > 0 ? (
          <div className='grid gap-4'>
            {filteredInquiries.map((inquiry) => (
              <Card 
                key={inquiry.id} 
                className='border-0 shadow-md hover:shadow-lg smooth-transition'
              >
                <CardContent className='p-6'>
                  <div className='flex justify-between items-start mb-4'>
                    <div className='flex items-start space-x-4 flex-1'>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        inquiry.status === 'pending' 
                          ? 'bg-amber-100' 
                          : 'bg-emerald-100'
                      }`}>
                        {inquiry.status === 'pending' ? (
                          <Mail className='w-6 h-6 text-amber-600' />
                        ) : (
                          <MailOpen className='w-6 h-6 text-emerald-600' />
                        )}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center space-x-3 mb-2'>
                          <h3 className='font-semibold text-lg text-gray-900'>{inquiry.name}</h3>
                          <Badge variant={inquiry.status === 'pending' ? 'default' : 'secondary'}>
                            {inquiry.status}
                          </Badge>
                        </div>
                        <p className='text-sm text-gray-600 mb-2'>
                          <a href={`mailto:${inquiry.email}`} className='text-emerald-600 hover:underline'>
                            {inquiry.email}
                          </a>
                        </p>
                        <h4 className='font-medium text-gray-900 mb-2'>{inquiry.subject}</h4>
                        <p className='text-gray-700 mb-3 whitespace-pre-wrap'>{inquiry.message}</p>
                        <p className='text-xs text-gray-500'>
                          Received: {formatDate(inquiry.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className='flex space-x-2 pt-4 border-t'>
                    <Button
                      size='sm'
                      className='bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                      onClick={() => openEmailClient(inquiry)}
                    >
                      <ExternalLink className='w-4 h-4 mr-2' />
                      Reply via Email
                    </Button>
                    
                    {inquiry.status === 'pending' ? (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => updateStatus(inquiry.id, 'replied')}
                      >
                        <CheckCircle className='w-4 h-4 mr-2' />
                        Mark as Replied
                      </Button>
                    ) : (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => updateStatus(inquiry.id, 'pending')}
                      >
                        <Clock className='w-4 h-4 mr-2' />
                        Mark as Pending
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className='border-0 shadow-md'>
            <CardContent className='p-12 text-center'>
              <Mail className='w-16 h-16 mx-auto mb-4 text-gray-400' />
              <p className='text-lg text-gray-600 mb-2'>No messages found</p>
              <p className='text-sm text-gray-500'>
                {searchQuery ? 'Try adjusting your search' : 'All caught up!'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SimpleInbox;
